import _ from 'lodash'
import { isOthersSelected } from 'mvscommoncomponents'
import appConfig from '../config/appConfig'
import {
  rfi2FinishedGoodSectionMapper,
  rfi2TrimSectionMapper,
} from '../globalComponents/RfiConstants'
import {
  BUTTON_APPROVE,
  BUTTON_APPROVE_AND_BYPASS_VFA,
  BUTTON_APPROVE_AND_START_VFA,
  BUTTON_APPROVE_BUT_HOLD,
  FACTORY_REGISTRATION_LICENSE,
  FIELD_CERTIFIED_FOR,
  FIELD_FABRIC_PRIMARY_TYPE,
  FIELD_SUBSTRATE_SUB_TYPE,
  FIELD_SUBSTRATE_TYPE,
  FIELD_SUPPLIER_COUNTRY_OF_PRODUCTION,
  FIELD_SUPPLIER_REGION_OF_PRODUCTION,
  FIELD_TRIM_BASE_MATERIAL,
  FIELD_TRIM_CONSTRUCTIONS,
  FIELD_TRIM_MATERIAL_CONTENT,
  FIELD_TRIM_MATERIAL_TYPE,
  FIELD_TRIM_PRIMARY_UOM,
  FIELD_TRIM_SUB_TYPE,
  FIELD_TRIM_TYPE,
  FINISHED_GOOD_ITEM_TYPE,
  FINISHED_GOOD_PRODUCT_SUB_TYPE,
  FINISHED_GOOD_PRODUCT_TYPE,
  MANUFACTURING_TYPE_ID_TRIM,
  MSG_VALIDATION_ERROR,
  RFI_TYPE_LOCATION,
  STATUS_APPROVE_AND_BYPASS_VFA,
  STATUS_APPROVE_AND_START_VFA,
  STATUS_APPROVE_BUT_HOLD,
  STATUS_APPROVED,
  STATUS_RFI3_APPROVED,
  TIMELINE_VIEW_NAME,
  TRIM_SUPPLIER_QUALIFICATION_PROGRAM,
} from './commonConstants'

/**
 * Form the status to be sent for RFI1/2
 * @param {*} currentVettingStatus
 */
export const getVettingStatusForSave = async (currentVettingStatusCode) => {
  return currentVettingStatusCode === '0'
    ? {
        vetting_status_code: 1,
        vetting_status: await getVettingStatusCode(1),
      }
    : null
}

/**
 * Get the vetting status value by passing status code for RFI1/2
 * @param {*} statusCode
 */
export const getVettingStatusCode = async (statusCode = null) => {
  const config = await appConfig()
  return statusCode ? config.mfelcm?.vettingStatusMap[statusCode] : null
}

/**
 * Get the vetting status code by passing status value for RFI1/2
 * @param {*} status
 */
export const getVettingStatusCodeFromStatusValue = async (status = null) => {
  const config = await appConfig()
  return status
    ? Object.keys(config.mfelcm.vettingStatusMap).find(
        (key) => config.mfelcm.vettingStatusMap[key] === status.toUpperCase(),
      )
    : null
}

/**
 * Get unique key name from statement & prepend word
 * Used to add key to html elements
 * @param {*} statement
 * @param {*} prependWord
 * @returns
 */
export const getUniqueKeyName = (statement, prependWord = null) => {
  return !prependWord
    ? statement
        .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
          return index === 0 ? word.toLowerCase() : word.toUpperCase()
        })
        .replace(/\s+/g, '')
    : `${prependWord.toLowerCase()}${statement
        .replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
          return word.toUpperCase()
        })
        .replace(/\s+/g, '')}`
}

/**
 * Build dropdown options
 * @param {*} rfiMasterData
 * @param {*} optionReference
 * @param {*} isDefaultDisabled
 * @param {*} addDefaultOption
 * @returns
 */
export const buildDropdownOptions = (
  rfiMasterData,
  optionReference,
  isDefaultDisabled = true,
  addDefaultOption = true,
) => {
  let masterList = exportMasterData(rfiMasterData, optionReference)

  let optionList = []
  if (masterList && masterList.length > 0) {
    if (addDefaultOption) {
      optionList = initializeDefaultMessageToSelect(isDefaultDisabled)
    }
    masterList.forEach((eachOption) => {
      optionList.push({
        id: eachOption,
        value: eachOption,
        label: eachOption,
      })
    })
  }

  return optionList
}

export const getObjectDataNode = (rfiObject, nodeDotPath) => {
  let stagedData = rfiObject

  nodeDotPath?.split('.')?.forEach((node) => {
    stagedData = stagedData[node]
  })
  return stagedData
}

/**
 * Get the required list from masterlist.[optionReference]
 * Note: optionReference contains {nodeDotPath, property}
 *       If only nodeDotPath => return the list of masterList.nodeDotPath
 *       If nodeDotPath + property => get list of objects from masterList.nodeDotPath
 *                                    for each object read the property, form list and return
 * If stagedData is list => Find unique elements and return array
 *               is object => return the object
 * @param {*} rfiMasterData
 * @param {*} optionReference
 * @returns
 */
export const exportMasterData = (rfiMasterData, optionReference) => {
  let stagedData = rfiMasterData
  optionReference?.nodeDotPath?.split('.')?.forEach((node) => {
    stagedData = stagedData[node]
  })

  if (optionReference?.property) {
    stagedData = stagedData?.map(
      (eachObject) => eachObject[optionReference.property],
    )
  }
  return stagedData?.length >= 0 ? Array.from(new Set(stagedData)) : stagedData
}

/**
 * Unify static & dynamic master data together for consolidated UI logic processing
 * @param {*} masterData
 * @param {*} dynamicMasterData
 * @returns
 */
export const unifyAllMasterData = (masterData, dynamicMasterData) => {
  let unifiedMasterData = { ...masterData }
  Object.entries(dynamicMasterData).forEach(([moduleNode, moduleData]) => {
    unifiedMasterData[moduleNode] = {
      ...unifiedMasterData[moduleNode],
      ...moduleData,
    }
  })
  return unifiedMasterData
}

/**
 * Initialize default message to any select
 * @param {*} isDefaultDisabled
 * @returns
 */
export function initializeDefaultMessageToSelect(isDefaultDisabled) {
  let options = []
  options.push({
    label: 'Please select an option',
    value: '',
    disabled: isDefaultDisabled,
  })
  return options
}

//--------T1 Product Hierarchy--------

/**
 * Get unique product types
 * @param {*} productHierarchyLevels
 * @returns
 */
export const getProductType = (productHierarchyLevels) => {
  let productTypes = []
  productTypes = productHierarchyLevels.map(
    (eachHierarchyLevel) => eachHierarchyLevel.product_type,
  )
  return Array.from(new Set(productTypes))
}

/**
 * Get unique product subtypes
 * @param {*} productHierarchyLevels
 * @param {*} productType
 * @returns
 */
export const getProductSubtypes = (productHierarchyLevels, productType) => {
  let productSubTypes = []
  productSubTypes = productHierarchyLevels
    .filter(
      (eachHierarchyLevel) => eachHierarchyLevel.product_type === productType,
    )
    .map((eachHierarchyLevel) => eachHierarchyLevel.product_sub_type)
  return Array.from(new Set(productSubTypes))
}

/**
 * Get unique merch types
 * @param {*} productHierarchyLevels
 * @param {*} productType
 * @param {*} productSubType
 * @returns
 */
export const getMerchTypes = (
  productHierarchyLevels,
  productType,
  productSubType,
) => {
  let merchTypes = []
  merchTypes = productHierarchyLevels
    .filter(
      (eachHierarchyLevel) =>
        eachHierarchyLevel.product_type === productType &&
        eachHierarchyLevel.product_sub_type === productSubType,
    )
    .map((eachHierarchyLevel) => eachHierarchyLevel.merchandise_type)
  return Array.from(new Set(merchTypes))
}

/**
 * Get unique item types
 * @param {*} productHierarchyLevels
 * @param {*} productType
 * @param {*} productSubType
 * @param {*} merchType
 * @returns
 */
export const getItemTypes = (
  productHierarchyLevels,
  productType,
  productSubType,
  merchType,
) => {
  let filteredHierarchy = []
  if (merchType) {
    filteredHierarchy = productHierarchyLevels.filter(
      (eachHierarchyLevel) =>
        eachHierarchyLevel.product_type === productType &&
        eachHierarchyLevel.product_sub_type === productSubType &&
        eachHierarchyLevel.merchandise_type === merchType,
    )
  } else {
    filteredHierarchy = productHierarchyLevels.filter(
      (eachHierarchyLevel) =>
        eachHierarchyLevel.product_type === productType &&
        eachHierarchyLevel.product_sub_type === productSubType,
    )
  }

  let itemTypes = filteredHierarchy.map(
    (eachHierarchyLevel) => eachHierarchyLevel.item_type,
  )
  return Array.from(new Set(itemTypes))
}

/**
 * Get product group by product type, subtype, merchType selected
 * @param {*} productHierarchyLevels
 * @param {*} productType
 * @param {*} productSubType
 * @param {*} merchType
 * @returns
 */
export const getProductGroup = (
  productHierarchyLevels,
  productType,
  productSubType,
  merchType,
) => {
  return productHierarchyLevels.filter(
    (eachHierarchyLevel) =>
      eachHierarchyLevel.product_type === productType &&
      eachHierarchyLevel.product_sub_type === productSubType &&
      eachHierarchyLevel.merchandise_type === merchType,
  )[0]?.group_names
}

//--------T2 Trim Hierarchy--------

/**
 * Get trim primary types
 * @param {*} trimHierarchyLevels
 * @param {*} filterPrimaryTypeList
 * @returns
 */
export const getTrimType = (
  trimHierarchyLevels,
  filterPrimaryTypeList,
  independentOfRfi1,
) => {
  //NOTE: filterPrimaryTypeList - is used to filter in only the primary types from the exhaustive list of capabilities.
  //eg. In TW Trim RFI2 we've a requirement to restrict only the selected types in RFI1
  let types
  if (independentOfRfi1) {
    types = trimHierarchyLevels
  } else {
    types = filterPrimaryTypeList
      ? trimHierarchyLevels.filter((e) =>
          filterPrimaryTypeList?.includes(e.trim_type_name),
        )
      : trimHierarchyLevels
  }
  let filteredTypes = types.map((h) => {
    return h.trim_type_name
  })

  return Array.from(new Set(filteredTypes))
}

/**
 * Get trim sub-type list
 * @param {*} trimHierarchyLevels
 * @param {*} primaryType
 * @returns
 */
export const getTrimSubType = (trimHierarchyLevels, primaryType) => {
  if (primaryType) {
    let subTypes = []
    subTypes = trimHierarchyLevels
      .filter((h) => h.trim_type_name === primaryType)[0]
      .trim_sub_types.map((h) => h.trim_sub_type_name)
    return Array.from(new Set(subTypes))
  }
}

/**
 * Get trim material type list
 * @param {*} trimHierarchyLevels
 * @param {*} primaryType
 * @returns
 */
export const getTrimMaterialType = (trimHierarchyLevels, primaryType) => {
  if (primaryType) {
    let materialTypes = []
    materialTypes = trimHierarchyLevels
      .filter((h) => h.trim_type_name === primaryType)[0]
      .trim_material_types?.map((h) => h.trim_material_type_name)
    return Array.from(new Set(materialTypes))
  }
}

/**
 * Get the trim construction type list
 * @param {*} trimHierarchyLevels
 * @param {*} primaryType
 * @returns
 */
export const getTrimConstructionType = (trimHierarchyLevels, primaryType) => {
  if (primaryType) {
    let constructionTypes = []
    constructionTypes = trimHierarchyLevels
      .filter((h) => h.trim_type_name === primaryType)[0]
      .trim_construction_types?.map((h) => h.trim_construction_type_name)
    return Array.from(new Set(constructionTypes))
  }
}

/**
 * Get the trim field's unit for the selected primaryType (& optional secondaryType)
 * @param {*} trimHierarchyLevels
 * @param {*} field
 * @param {*} primaryType
 * @returns
 */
export const getTrimTypeFieldUnit = (
  trimHierarchyLevels,
  field,
  primaryType,
) => {
  let unit = ''
  if (primaryType && field) {
    unit = trimHierarchyLevels.filter(
      (h) => h.trim_type_name === primaryType,
    )[0][field]
  }
  return unit
}

/**
 * Get product hierarchy dropdown options
 * @param {*} rfiMasterData
 * @param {*} rowData
 * @param {*} propertyId
 * @returns
 */
export const getDependentDropdownOptions = (
  rfiMasterData,
  rowData,
  control,
) => {
  let dropdownOptions = []
  switch (control?.uid.toLowerCase()) {
    //Trim fields
    case FIELD_TRIM_TYPE:
      dropdownOptions = buildDropdownOptions(
        getTrimType(
          rfiMasterData?.trim?.trim_types,
          rfiMasterData?.trim?.supplier_capabilities, //Sub list of trim primary types in RFI1
          control?.independent_of_rfi1,
        ),
        null,
        true,
        false,
      )
      break
    case FIELD_TRIM_SUB_TYPE:
      dropdownOptions = buildDropdownOptions(
        getTrimSubType(
          rfiMasterData?.trim?.trim_types,
          rowData?.[FIELD_TRIM_TYPE],
        ),
        null,
        true,
        false,
      )
      break
    case FIELD_TRIM_MATERIAL_CONTENT:
    case FIELD_TRIM_MATERIAL_TYPE:
    case FIELD_TRIM_BASE_MATERIAL:
      dropdownOptions = buildDropdownOptions(
        getTrimMaterialType(
          rfiMasterData?.trim?.trim_types,
          rowData?.[FIELD_TRIM_TYPE],
        ),
        null,
        true,
        false,
      )
      break
    case FIELD_TRIM_CONSTRUCTIONS:
      dropdownOptions = buildDropdownOptions(
        getTrimConstructionType(
          rfiMasterData?.trim?.trim_types,
          rowData?.[FIELD_TRIM_TYPE],
        ),
        null,
        true,
        false,
      )
      break
    case FIELD_TRIM_PRIMARY_UOM:
      dropdownOptions = buildDropdownOptions(
        getTrimTypeFieldUnit(
          rfiMasterData?.trim?.trim_types,
          FIELD_TRIM_PRIMARY_UOM,
          rowData?.[FIELD_TRIM_TYPE],
        ),
        null,
        true,
        false,
      )
      break

    //Packaging
    case FIELD_SUPPLIER_REGION_OF_PRODUCTION:
      dropdownOptions = buildDropdownOptions(
        getCountryRegions(
          rfiMasterData?.common?.countries,
          rowData?.[FIELD_SUPPLIER_COUNTRY_OF_PRODUCTION],
        ),
        null,
        true,
        false,
      )
      break
    case FIELD_SUBSTRATE_TYPE:
    case FIELD_CERTIFIED_FOR:
      dropdownOptions = buildDropdownOptions(
        getSubstrateType(rfiMasterData?.packaging?.primary_materials),
        null,
        true,
        false,
      )
      break
    case FIELD_SUBSTRATE_SUB_TYPE:
      dropdownOptions = buildDropdownOptions(
        getSubstrateSubType(
          rfiMasterData?.packaging?.primary_materials,
          rowData?.[FIELD_SUBSTRATE_TYPE],
        ),
        null,
        true,
        false,
      )
      break

    //Finished good fields
    case FINISHED_GOOD_PRODUCT_TYPE:
      dropdownOptions = buildDropdownOptions(
        getProductType(rfiMasterData?.common?.product_types),
        null,
        true,
        false,
      )
      break
    case FINISHED_GOOD_PRODUCT_SUB_TYPE:
      dropdownOptions = buildDropdownOptions(
        getProductSubtypes(
          rfiMasterData?.common?.product_types,
          rowData?.[FINISHED_GOOD_PRODUCT_TYPE],
        ),
        null,
        true,
        false,
      )
      break
    case FINISHED_GOOD_ITEM_TYPE:
      dropdownOptions = buildDropdownOptions(
        getItemTypes(
          rfiMasterData?.common?.product_types,
          rowData?.[FINISHED_GOOD_PRODUCT_TYPE],
          rowData?.[FINISHED_GOOD_PRODUCT_SUB_TYPE],
          null,
        ),
        null,
        true,
        false,
      )
      break

    //Fabric
    case FIELD_FABRIC_PRIMARY_TYPE:
      dropdownOptions = buildDropdownOptions(
        rfiMasterData,
        {
          nodeDotPath: control?.options,
        },
        true,
        false,
      )
      break

    default:
      console.log(
        'Unsupported hierarchy property while getting product hirarchy dropdown',
      )
  }
  return dropdownOptions
}

/**
 * Get Unit value for product hierarchy
 * @param {*} rfiMasterData
 * @param {*} rowData
 * @param {*} dependentPropertyUid
 * @param {*} unitPropertyUid
 * @returns
 */
export const getProductHierarchyUnitValue = (
  rfiMasterData,
  rowData,
  dependentPropertyUid,
  unitPropertyUid,
) => {
  let dependentPropertyValue = rowData[dependentPropertyUid]
  switch (dependentPropertyUid.toLowerCase()) {
    case FIELD_TRIM_TYPE:
      return getTrimTypeFieldUnit(
        rfiMasterData?.trim?.trim_types,
        unitPropertyUid,
        dependentPropertyValue,
      )
    default:
      console.log('Unsupported hierarchy property to fetch units')
  }
}

/**
 * Evaluate if hierarchy dropdown is enabled
 * @param {*} control
 * @param {*} sectionData
 * @returns
 */
export const isHierarchyDropdownEnabled = (control, sectionData) => {
  return control.enabled
    ? sectionData[control.enabled.field]
      ? true
      : false
    : true
}

//--------Strength Assessment--------

/**
 * Retrieves the strength assessment segments for a given primary capability.
 * @param {Object} saMasterData - The master data containing capability segments.
 * @param {string} primaryCapability - The primary capability to search for.
 * @returns {Array|undefined} The segments associated with the primary capability, or undefined if not found.
 */
export const getStrengthAssessmentByCapability = (
  saMasterData,
  primaryCapability,
) => {
  return saMasterData?.capability_segments.find(
    (c) => c.capability?.toLowerCase() === primaryCapability?.toLowerCase(),
  )?.segments
}

/**
 * Given a User Name, this will extract the First letter of FirstName & LastName.
 * If there is just FirstName extracts first two letters of the name. Even for International names it will work.
 * @param {*} userName
 * @returns
 */
export const getInitials = (userName) => {
  return `${userName
    .match(/(^\S\S?|\s\S)?/g)
    .join('')
    .match(/(^\S|\S$)?/g)
    .join('')
    .toUpperCase()}`
}

/**
 * Is control errored
 * @param {*} controlPath
 * @param {*} errorCache
 * @returns
 */
export const isControlError = (controlPath, errorCache) => {
  return errorCache && errorCache.controls && controlPath in errorCache.controls
}

/**
 * Get control error
 * @param {*} controlPath
 * @param {*} errorCache
 * @returns
 */
export const getControlError = (controlPath, errorCache) => {
  return errorCache?.controls?.[controlPath]
}

/**
 * Get the tab names that are errored from tabIDs
 * @param {*} erroredTabIds
 * @param {*} rfiTemplateTabs
 * @returns
 */
export const getErroredTabs = (erroredTabIds, rfiTemplateTabs) => {
  return erroredTabIds?.map((eachTabId) => {
    return rfiTemplateTabs.filter((eachTab) => eachTab.uid === eachTabId)[0]
      ?.tab_name
  })
}

/**
 * Get RFI Banner error message
 * @param {*} erroredTabIds
 * @param {*} rfiTemplateTabs
 * @returns
 */
export const getRfiBannerErrorMessage = (erroredTabIds, rfiTemplateTabs) => {
  let errorTabs = getErroredTabs(erroredTabIds, rfiTemplateTabs)
  if (errorTabs) {
    return MSG_VALIDATION_ERROR.replace('<TABS>', errorTabs.join(', '))
  }
}

/**
 * Get section display names from master data - by RFI step node & section in it
 * @param {*} rfiSupplierMasterData
 * @param {*} rfiStepNode
 * @param {*} section
 * @returns
 */
export const getAllSectionDisplayNames = (
  rfiSupplierMasterData,
  rfiStepNode,
  section,
) => {
  return rfiSupplierMasterData?.[rfiStepNode][section]?.map(
    (eachGroup) => eachGroup.display_name,
  )
}

/**
 * Get the X or Y axis data for a section->groupName->field (X or Y axis) for a reaction grid
 * @param {*} masterData
 * @param {*} modelData
 * @param {*} section
 * @param {*} groupName
 * @param {*} field
 * @param {*} isreadOnly
 * @returns
 */
export const getReactionGridDataField = (
  masterData,
  modelData,
  section,
  groupName,
  field,
  isreadOnly,
) => {
  let dataField = null
  if (!isreadOnly) {
    let groupFields = masterData[section]
    dataField = groupFields.length
      ? groupFields?.filter(
          (eachGroup) => eachGroup.group_name === groupName,
        )[0]?.[field]
      : groupFields[field]
  } else {
    if (field === 'display_name') {
      dataField = modelData[section]?.filter(
        (eachGroup) => eachGroup.group_name === groupName,
      )[0]?.display_name
    }
  }
  return dataField
}

/**
 * Fetch Group Names for the reaction grid's name
 *
 * @param {*} modelData from which group_name has to be extracted
 * @param {*} sectionName name of the section from which group_name is extracted
 * @param {*} subSectionName name of the subsection from which group_name is extracted
 * @param {*} displayName name of the display based on which group_name is extracted
 * @returns group_name
 */
export const fetchGroupName = (
  modelData,
  sectionName,
  subSectionName,
  displayName,
) => {
  return modelData[sectionName][subSectionName].filter(
    (groups) => groups.display_name === displayName,
  )[0]?.group_name
}

/**
 * This method is used to get the Display names for the reaction grid groups, to display in the autocomplete input box.
 *
 * @param {*} propertyId name of the property for which the display name is retrieved
 * @param {*} masterData master data from which the display name has to be retrieved
 * @param {*} sectionName name of the section in master data
 * @returns names that are to be displayed
 */
export const getDropdownOptions = (propertyId, masterData, sectionName) => {
  return buildDropdownOptions(
    getAllSectionDisplayNames(masterData, sectionName, propertyId),
    null,
    false,
    false,
  )
}

/**
 * This methods is used to get the all previous the years value until the present year based on the value passed.
 *
 * @param {*} numberOfYears number of years value that is to be fetched
 * @returns list of values with all the years as label and value pairs.
 */
export const fetchLastXYearsDropdown = (numberOfYears) => {
  let yearOptions = []
  let currentYear = new Date().getFullYear()
  let i
  yearOptions = initializeDefaultMessageToSelect(true)
  for (i = currentYear; i > currentYear - numberOfYears; i--) {
    yearOptions.push({
      value: i,
      label: i,
    })
  }
  return yearOptions
}

/**
 * Format the question number of RFI3 questions
 * @param {*} number
 * @returns
 */
export const formatQuestionNumber = (number) => {
  return ('0' + number).slice(-2)
}

/**
 * Get the visibility of the control/route based on controlName, controlType and the user settings passed
 * @param {*} controlName
 * @param {*} controlType
 * @param {*} userSettings
 */
export function getControlVisibility(controlName, controlType, userSettings) {
  let isVisible = true
  if (userSettings) {
    let filteredUserSetting = userSettings.filter(
      (eachUserSetting) =>
        eachUserSetting.componentType === controlType &&
        eachUserSetting.componentName === controlName,
    )
    isVisible =
      filteredUserSetting.length === 0 ? true : filteredUserSetting[0].isVisible
  }
  return isVisible
}

/**
 * Copy to clipboard and show the toaster message
 * @param {*} copyText
 */
export const copyToClipboard = (copyText, makeToast) => {
  navigator.clipboard
    .writeText(copyText)
    .then(() => {
      makeToast({
        type: 'success',
        message: `"${copyText}" copied to your clipboard`,
        autoHideDuration: 5000,
      })
    })
    .catch((err) => {
      console.log(err.message)
    })
}

/**
 * Convert snake case dot path to camel case dot path
 * @param {*} dotSnakeString
 * @returns
 */
export const dotSnakeToDotCamel = (dotSnakeString) => {
  return dotSnakeString
    ?.split('.')
    .map((eachLevel) => _.camelCase(eachLevel))
    .join('.')
}

/**
 * Is "Other"/"Others" part of array
 * @param {*} valueList
 */
export const isOthersInArray = (valueList) => {
  return valueList?.some((eachValue) => isOthersSelected(eachValue))
}

/**
 * Get product types list for supplier and location
 * @param {*} rfiType
 * @param {*} rfiObject
 * @returns
 */
export const getProductTypesList = (rfiType, rfiObject) => {
  if (rfiObject?.manufacturing_type_id === 7) {
    return [
      ...new Set(
        rfiObject?.dynamic_sections?.business_capability?.top_fabrics_sold?.top_fabrics_sold_list?.map(
          (fs) => fs?.[FIELD_FABRIC_PRIMARY_TYPE],
        ),
      ),
    ]
  } else if (
    rfiType?.toLowerCase() === RFI_TYPE_LOCATION &&
    rfiObject?.manufacturing_type_id === 8
  ) {
    return [
      ...new Set(
        rfiObject?.dynamic_sections?.business_capability?.factory_capabilities?.factory_capability_list?.map(
          (fc) => fc?.factory_capability?.product_type,
        ),
      ),
    ]
  } else {
    return [
      ...new Set(
        rfiObject?.dynamic_sections?.business_capability?.total_product_capabilities?.total_product_capability_list?.map(
          (pc) => pc?.product_capability?.product_type,
        ),
      ),
    ]
  }
}

//--------Country--------

/**
 * Get country name
 * @param {*} rfiObject
 * @param {*} rfi
 */
export const getCountryName = (rfiObject, rfi) => {
  let country = ''
  if (rfi?.toUpperCase() === 'RFI1') {
    country =
      rfiObject?.fixed_sections?.supplier_contact_information?.supplier_address
        ?.country
  } else {
    country =
      rfiObject?.dynamic_sections?.other_information?.additional_factory_information?.factory_address?.address?.find(
        (obj) =>
          obj.labels?.some(
            (label) =>
              label.toLowerCase() === 'country_name' ||
              label.toLowerCase() === 'country',
          ),
      )?.values[0]
  }

  return country ? country.toUpperCase() : ''
}

/**
 * Get ROP from Country
 * @param {*} rfiObject
 * @param {*} countryName
 * @returns
 */
export const getCountryRegions = (rfiObject, countryName) => {
  return rfiObject.filter((c) => c.country === countryName)[0]?.rop
}

//--------Packaging Equipment--------

/**
 * Get names of packaging equipment capabilities from the master data (Packaging).
 * @param {Object} masterData - The master data object containing packaging information.
 * @returns {*} An array of packaging equipment names.
 */
export const getPackagingEquipmentCapabilities = (masterData) => {
  return masterData.packaging.packaging_equipments?.map(
    (eachPackagingEquipment) => eachPackagingEquipment.name,
  )
}

/**
 * Retrieves the packaging equipment options based on the specified capability name (Packaging).
 * @param {Object} masterData - Master data containing packaging information.
 * @param {string} capability - Capability name to filter packaging equipment.
 * @returns {*} - An array of options for the specified capability, or undefined if not found.
 */
export const getPackagingEquipmentOptionsByCapability = (
  masterData,
  capability,
) => {
  return masterData.packaging.packaging_equipments.find(
    (c) => c.name === capability,
  )?.options
}

/**
 * Retrieves the UID of a packaging capability based on its name.
 * @param {Object} masterData - The master data object containing packaging information.
 * @param {string} capabilityName - The name of the packaging capability to find.
 * @returns {*} The UID of the packaging capability, or undefined if not found.
 */
export const getPackagingCapabilityUidFromName = (
  masterData,
  capabilityName,
) => {
  return masterData.packaging.packaging_equipments.filter(
    (c) => c.name === capabilityName,
  )[0]?.uid
}

/**
 * Get Substrate type
 * @param {*} primaryMaterials
 * @returns
 */
export const getSubstrateType = (primaryMaterials) => {
  return primaryMaterials?.map(
    (primaryMaterial) => primaryMaterial.substrate_type_name,
  )
}

/**
 * Get Substrate type
 * @param {*} primaryMaterials
 * @param {*} substrateType
 * @returns
 */
export const getSubstrateSubType = (primaryMaterials, substrateType) => {
  return Array.from(
    new Set(
      primaryMaterials
        .filter((p) => substrateType?.toUpperCase() === p.substrate_type_name)
        ?.flatMap((p) =>
          p.substrate_sub_types?.map((p) => p.substrate_sub_type_name),
        ),
    ),
  )
}

/**
 * Returns the appropriate button name based on the given approval status option.
 * @param {string} option - The approval status option.
 */
export const getApprovalButtonName = (option) => {
  switch (option.toUpperCase()) {
    case STATUS_APPROVE_BUT_HOLD.toUpperCase():
      return BUTTON_APPROVE_BUT_HOLD
    case STATUS_APPROVE_AND_START_VFA.toUpperCase():
      return BUTTON_APPROVE_AND_START_VFA
    case STATUS_APPROVE_AND_BYPASS_VFA.toUpperCase():
      return BUTTON_APPROVE_AND_BYPASS_VFA
    case STATUS_RFI3_APPROVED.toUpperCase():
      return BUTTON_APPROVE
    case STATUS_APPROVED.toUpperCase():
      return BUTTON_APPROVE
    default:
      console.log('Invalid approval status')
  }
}

// --- PDF Data Mapping ---
/**
 * Returns the controls or sub_sections for a section.
 * @param {Object} section - The section object.
 * @returns {Array} The controls or sub_sections array.
 */
export function getSectionControlsList(section) {
  return section.sub_sections ? section.sub_sections : section.controls
}

/**
 * Flattens nested controls from sections or controls array.
 * @param {Array} sectionsOrControl - Array of sections or controls.
 * @returns {Array} Flattened controls array.
 */
export function flattenSectionControls(sectionsOrControl = []) {
  if (
    sectionsOrControl.length &&
    sectionsOrControl[0].uid &&
    !sectionsOrControl[0].data
  ) {
    return sectionsOrControl
  }
  let controls = []
  sectionsOrControl.forEach((item) => {
    if (item.data && Array.isArray(item.data.controls)) {
      controls = controls.concat(item.data.controls)
    }
  })
  return controls
}

/**
 * Maps section data and controls to the master data format for rendering.
 * @param {Object} mainobject - The main object containing section data.
 * @param {Array} sectionsOrControl - Array of sections or controls.
 * @returns {Object} The mapped master data.
 */
export function mapSectionDataToMasterData(mainobject, sectionsOrControl) {
  const controls = flattenSectionControls(sectionsOrControl)
  const controlMap = {}
  controls.forEach((ctrl) => {
    controlMap[ctrl.uid] = ctrl.label_name
  })

  const finalData = {}

  Object.entries(mainobject).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length && typeof value[0] === 'object') {
      finalData[key] = value.map((rowObj) => {
        return Object.entries(rowObj)
          .filter(([k, v]) => v !== null && v !== undefined && k in controlMap)
          .map(([k, v]) => ({
            value: v,
            label: controlMap[k] || k,
            uid: k,
          }))
      })
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      finalData[key] = value
    } else {
      finalData[key] = {
        value,
        label: controlMap[key] || key,
        uid: key,
      }
    }
  })

  return finalData
}

/**
 * Resolves a value from an object using a dot-separated path string.
 * @param {Object} obj - The object to resolve from.
 * @param {string} path - The dot-separated path string.
 * @returns {*} The resolved value or undefined.
 */
export function resolveMasterOptionPath(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

/**
 * Filters out non-main fields from section data.
 * @param {Object} sectionData - The section data object.
 * @returns {Object} The filtered section data.
 */
export function filterSectionMainFields(sectionData) {
  const excludeKeys = [
    'rfi3_answers',
    'rfi3_questions_and_answers',
    'rfi3_files',
    'sectionsOrControl',
  ]
  return Object.fromEntries(
    Object.entries(sectionData).filter(([key]) => !excludeKeys.includes(key)),
  )
}

/**
 * Key mapping for factory capabilities.
 * @type {Object}
 */
export const factory_capabilities_key_mapping = {
  product_type: 'Product Type',
  product_sub_type: 'Product Sub-Type',
  merchandise_type: 'Merch Type',
  item_type: 'Item type',
  daily_factory_output: 'Daily Factory Output (units)',
  automation_level: 'Automation Level',
  capacity_percentage_open_for_target: 'Percent of Capacity open for Target',
  capacity_percentage_penetrated_by_other_customers:
    'Percent of capacity penetrated by other customers',
}

/**
 * Formats factory capabilities for PDF rendering.
 * @param {Array} data - Array of factory capability objects.
 * @param {Object} keyMapping - Key mapping for capability labels.
 * @returns {Array} Formatted factory capabilities.
 */
export function formatFactoryCapabilities(data = [], keyMapping) {
  return data.map((item) => {
    const factoryCapability = item.factory_capability
    return Object.entries(factoryCapability)
      .filter(([key]) => key in keyMapping)
      .map(([key, value]) => ({
        label: keyMapping[key],
        uid: key,
        value: value,
      }))
  })
}

/**
 * Formats employee strength data for PDF rendering.
 * @param {Array} employeeData - Array of employee strength objects.
 * @returns {Array} Formatted employee strength list.
 */
export function formatEmployeeStrengthList(employeeData = []) {
  return employeeData.map((item) => [
    {
      label: 'Employee Designation',
      uid: 'employee_designation',
      value: item.employee_designation,
    },
    {
      label: 'Number of Employees',
      uid: 'number_of_employees',
      value: item.number_of_employees,
    },
  ])
}

/**
 * Calculates the total number of employees from the employee strength list.
 * @param {Array} employeeStrengthList - Array of employee strength objects.
 * @returns {number} Total number of employees.
 */
export function calculateTotalEmployees(employeeStrengthList = []) {
  return employeeStrengthList.reduce(
    (sum, emp) => sum + (Number(emp.number_of_employees) || 0),
    0,
  )
}

/**
 * Formats address data for PDF rendering.
 * @param {Array} addressArr - Array of address objects.
 * @returns {Array} Formatted address rows.
 */
export function formatAddressRows(addressArr = []) {
  const row = []
  addressArr.forEach((item) => {
    if (Array.isArray(item.labels) && Array.isArray(item.values)) {
      if (
        item.labels.length === 2 &&
        item.labels[0] === 'latitude' &&
        item.labels[1] === 'longitude'
      ) {
        row.push({
          label: 'Latitude',
          uid: 'latitude',
          value: item.values[0] ?? '',
        })
        row.push({
          label: 'Longitude',
          uid: 'longitude',
          value: item.values[1] ?? '',
        })
      } else {
        row.push({
          label: item.labels[0],
          uid: item.labels[0],
          value: item.values[0] ?? '',
        })
      }
    }
  })
  return [row, []]
}

/**
 * Checks if a field is empty for PDF rendering.
 * Handles strings, arrays, objects, null, and undefined.
 * @param {*} val - The value to check.
 * @returns {boolean} True if the field is empty, false otherwise.
 */
export const isPdfFieldEmpty = (val) => {
  if (val === null || val === undefined) return true
  if (typeof val === 'string' && val.trim() === '') return true
  if (Array.isArray(val)) {
    if (val.length === 0) return true
    return val.every(isPdfFieldEmpty)
  }
  if (typeof val === 'object') {
    if ('value' in val) return isPdfFieldEmpty(val.value)
    const keys = Object.keys(val)
    if (keys.length === 0) return true
    return keys.every((k) => isPdfFieldEmpty(val[k]))
  }
  return false
}

/**
 * Label and UID mapping for packaging equipment fields used in PDF rendering.
 * @type {Object}
 */
const PACKAGING_LABELS = {
  color: { label: 'color', uid: 'color' },
  type: { label: 'Type', uid: 'type' },
  uph: { label: 'UPH', uid: 'uph' },
  quantity_of_equipments: {
    label: 'Quantity of Equipments',
    uid: 'quantityOfEquipments',
  },
  age: { label: 'Age (In Years)', uid: 'age' },
  production_maximum_size: {
    label: 'Production maximum size',
    uid: 'productionMaximumSize',
  },
  production_minimum_size: {
    label: 'Production minimum size',
    uid: 'productionMinimumSize',
  },
  unit_of_measurement: {
    label: 'Unit of Measurement',
    uid: 'unitOfMeasurement',
  },
  remarks: { label: 'Remarks', uid: 'remarks' },
}

/**
 * Formats a size object into a label/value object for PDF rendering.
 * @param {Object} sizeObj - The size object with length and width properties.
 * @param {string} label - The label for the size field.
 * @param {string} uid - The UID for the size field.
 * @returns {Object|null} The formatted size object or null if invalid.
 */
function formatSize(sizeObj, label, uid) {
  if (!sizeObj) return null
  const length = sizeObj[Object.keys(sizeObj)[0]]
  const width = sizeObj[Object.keys(sizeObj)[1]]
  if (length == null && width == null) return null
  return {
    label,
    uid,
    value: `${length ?? ''} x ${width ?? ''}`.trim(),
  }
}

/**
 * Formats packaging equipment data into a structured format for PDF rendering.
 * Ensures main sections and sub-sections are ordered and labeled as required.
 * Adds metadata for section and sub-section labels and row indices.
 * @param {Object} input - The raw packaging equipment data.
 * @returns {Object} The formatted packaging equipment data for PDF.
 */
export function formatPackagingEquipmentData(input) {
  const output = {}

  // Always copy equipment_capability_list as is
  if (input.equipment_capability_list) {
    output.equipment_capability_list = input.equipment_capability_list
  }

  // Define the desired order for main sections and sub-sections
  const MAIN_SECTION_ORDER = [
    'print_pre_press',
    'print_press',
    'print_color_control',
    'print_post_press',
  ]
  const SECTION_LABELS = {
    print_pre_press: 'Print Pre-Press',
    print_press: 'Print - Press',
    print_color_control: 'Color Control',
    print_post_press: 'Print Post-Press',
  }
  const SUB_SECTION_ORDER = {
    print_pre_press: [
      'digital_printer',
      'mock_up_sample_cutting',
      'plate_making',
    ],
    print_press: [
      'offset_printing',
      'digital_printing',
      'flexo',
      'gravure',
      'silk_screen',
    ],
    print_color_control: [
      'light_booth',
      'barcode_reader',
      'spectrodensitometer',
    ],
    print_post_press: [
      'coating',
      'spot_uv',
      'lamination',
      'pp_lamination',
      'foil_stamping',
      'embossing_or_debossing',
      'die_cutting',
      'gluing',
    ],
  }
  const SUBSECTION_LABELS = {
    offset_printing: 'Offset Printing',
    digital_printing: 'Digital Printing',
    flexo: 'Flexo',
    gravure: 'Gravure',
    silk_screen: 'Silk Screen',
    light_booth: 'Light Booth',
    barcode_reader: 'Barcode Reader',
    spectrodensitometer: 'Spectrodensitometer',
    coating: 'Coating',
    spot_uv: 'Spot UV',
    lamination: 'Lamination',
    pp_lamination: 'PP Lamination',
    foil_stamping: 'Foil Stamping',
    embossing_or_debossing: 'Embossing/Debossing',
    die_cutting: 'Die Cutting',
    gluing: 'Gluing',
    digital_printer: 'Digital Printer',
    mock_up_sample_cutting: 'Mock Up Sample Cutting',
    plate_making: 'Plate Making',
  }

  MAIN_SECTION_ORDER.forEach((mainKey) => {
    const mainVal = input[mainKey]
    if (!mainVal || typeof mainVal !== 'object') return

    output[mainKey] = { __sectionLabel: SECTION_LABELS[mainKey] || mainKey }

    const subOrder = SUB_SECTION_ORDER[mainKey] || []
    subOrder.forEach((subKey) => {
      const arr = mainVal[subKey]
      if (!Array.isArray(arr)) return
      output[mainKey][subKey] = arr.map((item, idx) => {
        const row = []
        if (item.color) {
          row.push({
            label: PACKAGING_LABELS.color.label,
            uid: PACKAGING_LABELS.color.uid,
            value: item.color,
          })
        }
        if (item.type !== undefined) {
          row.push({
            label: PACKAGING_LABELS.type.label,
            uid: PACKAGING_LABELS.type.uid,
            value: item.type,
          })
        }
        if (item.uph !== undefined) {
          row.push({
            label: PACKAGING_LABELS.uph.label,
            uid: PACKAGING_LABELS.uph.uid,
            value: item.uph,
          })
        }
        if (item.quantity_of_equipments !== undefined) {
          row.push({
            label: PACKAGING_LABELS.quantity_of_equipments.label,
            uid: PACKAGING_LABELS.quantity_of_equipments.uid,
            value: item.quantity_of_equipments,
          })
        }
        if (item.age !== undefined) {
          row.push({
            label: PACKAGING_LABELS.age.label,
            uid: PACKAGING_LABELS.age.uid,
            value: item.age,
          })
        }
        if (item.production_maximum_size) {
          const size = formatSize(
            item.production_maximum_size,
            PACKAGING_LABELS.production_maximum_size.label,
            PACKAGING_LABELS.production_maximum_size.uid,
          )
          if (size) row.push(size)
        }
        if (item.production_minimum_size) {
          const size = formatSize(
            item.production_minimum_size,
            PACKAGING_LABELS.production_minimum_size.label,
            PACKAGING_LABELS.production_minimum_size.uid,
          )
          if (size) row.push(size)
        }
        if (item.unit_of_measurement !== undefined) {
          row.push({
            label: PACKAGING_LABELS.unit_of_measurement.label,
            uid: PACKAGING_LABELS.unit_of_measurement.uid,
            value: item.unit_of_measurement,
          })
        }
        if (item.remarks !== undefined) {
          row.push({
            label: PACKAGING_LABELS.remarks.label,
            uid: PACKAGING_LABELS.remarks.uid,
            value: item.remarks,
          })
        }
        row.__rowIndex = idx + 1
        row.__subSectionLabel = SUBSECTION_LABELS[subKey] || subKey
        return row
      })
    })
  })

  // If there are any other keys not in MAIN_SECTION_ORDER, add them at the end in original order
  Object.entries(input).forEach(([mainKey, mainVal]) => {
    if (
      mainKey === 'equipment_capability_list' ||
      MAIN_SECTION_ORDER.includes(mainKey) ||
      typeof mainVal !== 'object'
    ) {
      return
    }
    output[mainKey] = mainVal
  })
  return output
}

// --- TRIM: Total Trim Capabilities ---
export function formatTotalTrimCapabilities(capabilityList = []) {
  return capabilityList.map((entry, index) => {
    const rows = [
      {
        label: 'Trim Type',
        uid: 'primary_trim_type',
        value: entry.primary_trim_type,
      },
      {
        label: 'Trim Sub-Type',
        uid: 'trim_sub_type',
        value: entry.trim_sub_type,
      },
      {
        label: 'Sample Lead time',
        uid: 'sample_lead_time',
        value: entry.sample_lead_time,
      },
      {
        label: 'Counter Sample Lead Time',
        uid: 'counter_sample_lead_time',
        value: entry.counter_sample_lead_time,
      },
      {
        label: 'Production Lead Time',
        uid: 'production_lead_time',
        value: entry.production_lead_time,
      },
      {
        label: 'Does this Item have a minimum order quantity',
        uid: 'does_item_have_minimum_order_quantity',
        value: entry.does_item_have_minimum_order_quantity,
      },
    ]

    if (entry.does_item_have_minimum_order_quantity === 'Yes') {
      rows.push({
        label: 'Minimum order quantity (MOQ)',
        uid: 'minimum_order_quantity',
        value: entry.minimum_order_quantity,
      })
    }

    // Handle product_specifications
    const specs = Array.isArray(entry.product_specifications)
      ? entry.product_specifications.map((spec, idx) => {
          const specRow = []
          Object.entries(spec).forEach(([k, v]) => {
            if (k === 'color' && Array.isArray(v)) {
              specRow.push({
                label: 'Colour',
                uid: 'color',
                value: v.join(', '),
              })
            } else {
              specRow.push({
                label: k
                  .replace(/_/g, ' ')
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
                uid: k,
                value: v,
              })
            }
          })
          specRow.__rowIndex = idx + 1
          specRow.__subSectionLabel = 'Product Specification'
          return specRow
        })
      : []

    return [...rows, ...specs]
  })
}

// --- TRIM: Sub-Contracted List Formatter ---
export function formatTrimSubContractedList(
  factoryList = [],
  type = 'production',
) {
  return factoryList.map((factory, index) => {
    const outerInfo = [
      {
        label: 'Legal Manufacturing Name',
        uid: 'legal_manufacturing_name',
        value: factory.legal_manufacturing_name,
      },
      {
        label: 'Address',
        uid: 'address',
        value: factory.address,
      },
      {
        label: 'Key Contact Email',
        uid: 'key_contact_email',
        value: factory.key_contact_email,
      },
    ]

    const subList =
      factory.sub_contracted_list?.map((subItem, idx) => {
        const subRows = []
        Object.entries(subItem).forEach(([k, v]) => {
          subRows.push({
            label: k
              .replace(/_/g, ' ')
              .replace(/\b\w/g, (c) => c.toUpperCase()),
            uid: k,
            value: Array.isArray(v) ? v.join(', ') : v,
          })
        })
        subRows.__rowIndex = idx + 1
        subRows.__subSectionLabel =
          type === 'production'
            ? 'Production Subcontracted Item'
            : 'Service Subcontracted Item'
        return subRows
      }) || []

    return [...outerInfo, ...subList.flat()]
  })
}

// --- TRIM: Research and Development Contacts ---
export function formatResearchContactInfoList(rndList = []) {
  return rndList.flatMap((entry, idx) => {
    const base = [
      {
        label: 'Have R&D Division',
        uid: 'have_research_and_development_division',
        value: entry.have_research_and_development_division,
      },
    ]
    const contacts =
      entry.research_contact_information_list?.map((c, i) => [
        {
          label: 'Name',
          uid: 'name',
          value: c.name,
        },
        {
          label: 'Email Id',
          uid: 'email_id',
          value: c.email_id,
        },
      ]) || []

    return [base, ...contacts]
  })
}

// --- Finished Good: Material Capabilities ---
export function formatMaterialCapabilityList(data = []) {
  return data.map((item, idx) => [
    {
      label: 'Material Group',
      uid: 'material_group',
      value: item.material_group,
    },
    {
      label: 'Material Capabilities',
      uid: 'material_capabilities',
      value: Array.isArray(item.material_capabilities)
        ? item.material_capabilities.join(', ')
        : item.material_capabilities,
    },
  ])
}

// --- Finished Good: In-House Manufacturing Process ---
export function formatInHouseManufacturingList(data = []) {
  return data.map((item, idx) => [
    {
      label: 'Process',
      uid: 'process_name',
      value: item.process_name,
    },
    {
      label: 'Capacity per Day (units)',
      uid: 'capacity_per_day',
      value: item.capacity_per_day,
    },
    {
      label: 'Is Sub-Contracted?',
      uid: 'is_process_sub_contracted',
      value: item.is_process_sub_contracted,
    },
    {
      label: 'Sub-Contract Factory Name',
      uid: 'sub_contracted_factory_name',
      value: item.sub_contracted_factory_name,
    },
    {
      label: 'Address of Sub-Contracted Factory',
      uid: 'sub_contracted_factory_address',
      value: item.sub_contracted_factory_address,
    },
  ])
}

// --- Finished Good: Internal Testing Methods ---
export function formatInternalTestingList(data = []) {
  return data.map((item, idx) => [
    {
      label: 'Test',
      uid: 'test',
      value: Array.isArray(item.test) ? item.test.join(', ') : item.test,
    },
    {
      label: 'Comments',
      uid: 'comments',
      value: item.comments,
    },
  ])
}

// --- Finished Good: Packaging Capabilities ---
export function formatPackagingCapabilityList(data = []) {
  return data.map((item, idx) => [
    {
      label: 'Primary Packaging Format',
      uid: 'primary_packaging_format',
      value: item.primary_packaging_format,
    },
    {
      label: 'Comments',
      uid: 'comments',
      value: item.comments,
    },
  ])
}

// --- Finished Good: Additional Business Capabilities ---
export function formatAdditionalBusinessCapabilities(data = {}) {
  return Object.entries(data).map(([k, v]) => ({
    label: k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    uid: k,
    value: v,
  }))
}

// --- Finished Good: Beauty Formulation Capabilities ---
export function formatBeautyFormulationCapabilities(data = {}) {
  const output = {}
  for (const [key, value] of Object.entries(data)) {
    if (!Array.isArray(value)) continue
    output[key] = {
      __sectionLabel: key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase()),
    }
    value.forEach((group, i) => {
      const rows = group.capabilities.map((cap, idx) => ({
        label: cap.key,
        value: cap.value,
        uid: cap.key,
        __rowIndex: idx + 1,
        __subSectionLabel: group.display_name,
      }))
      output[key][group.group_name] = rows
    })
  }
  return output
}

// --- Finished Good: Generic Contact List Formatter ---
export function formatContactList(data = []) {
  return data.map((item) => [
    {
      label: 'Employee Designation',
      uid: 'employee_designation',
      value: item.employee_designation,
    },
    {
      label: 'Name',
      uid: 'name',
      value: item.name,
    },
    {
      label: 'Email',
      uid: 'email_id',
      value: item.email_id,
    },
    {
      label: 'Phone',
      uid: 'phone_number',
      value: item.phone_number,
    },
    {
      label: 'Speaks English',
      uid: 'speaks_english',
      value: item.speaks_english,
    },
  ])
}

// --- Finished Good: Key Customers & Private Label Retailers ---
export function formatCustomerList(data = [], type = 'key_customer') {
  return data.map((item) => [
    {
      label: type === 'key_customer' ? 'Retailer/Brand Name' : 'Customer Name',
      uid: 'retailer_name',
      value: item.retailer_name || item.customer_name,
    },
    {
      label: '% of Annual Sales in USD',
      uid: 'annual_sales_perc_in_usd',
      value: item.annual_sales_perc_in_usd ?? '',
    },
    {
      label: '% of Annual Sales in Units',
      uid: 'annual_units',
      value:
        item.annual_sales_perc_in_units ??
        item.total_production_capacity_perc ??
        '',
    },
  ])
}

/**
 * Generates the structured data for all sections/tabs of the RFI3 PDF document.
 * @param {Object} rfi3Template - The RFI3 template object.
 * @param {Object} rfi3Data - The RFI3 data object.
 * @param {Object} rfiMasterData - The RFI master data object.
 * @returns {Array} Array of tab data for PDF rendering.
 */
export function generatePdfSectionsData(
  rfi3Template,
  rfi3Data,
  rfiMasterData,
  manufacturing_type,
) {
  return rfi3Template.dynamic_sections?.tabs.map((tab) => ({
    parentId: tab.uid,
    parentName: tab.tab_name,
    data: tab.sections.map((section) => {
      const formattedRfi3Data = getObjectDataNode(
        rfi3Data,
        manufacturing_type === 'trim'
          ? rfi2TrimSectionMapper[section.uid]
          : rfi2FinishedGoodSectionMapper[section.uid],
      )

      const controls = getSectionControlsList(section)
      const mainObj = filterSectionMainFields(formattedRfi3Data || {})
      const finalData = mapSectionDataToMasterData(mainObj, controls)

      let masterData
      if (section.uid === 'factory_capabilities') {
        masterData = {
          factory_capabilities_list: formatFactoryCapabilities(
            formattedRfi3Data?.factory_capability_list,
            factory_capabilities_key_mapping,
          ),
        }
      } else if (section.uid === 'employee_information') {
        masterData = {
          ...finalData,
          employee_strength_list: formatEmployeeStrengthList(
            formattedRfi3Data?.employee_strength_list,
          ),
          total_employees: [
            [
              {
                label: 'Total Employees',
                uid: 'total_employees',
                value: calculateTotalEmployees(
                  formattedRfi3Data?.employee_strength_list,
                ),
              },
            ],
          ],
          total_employees_by_gender: [
            [
              {
                label: 'Total Male Employees',
                uid: 'total_male_employees',
                value: finalData?.total_employees_by_gender?.number_of_male,
              },
              {
                label: 'Total Female Employees',
                uid: 'total_female_employees',
                value: finalData?.total_employees_by_gender?.number_of_female,
              },
            ],
          ],
          total_employees_by_demographics: [
            [
              {
                label: 'Total Local Employees',
                uid: 'total_local_employees',
                value:
                  finalData?.total_employees_by_demographics?.local_employees,
              },
              {
                label: 'Total Foreign Employees',
                uid: 'total_foreign_employees',
                value:
                  finalData?.total_employees_by_demographics?.foreign_employees,
              },
            ],
          ],
        }
      } else if (section.uid === 'additional_factory_information') {
        const addressArr = finalData?.factory_address?.address
        masterData = {
          ...finalData,
          factory_address: formatAddressRows(addressArr || []),
        }
      } else if (section.uid === 'packaging_equipment') {
        masterData = formatPackagingEquipmentData(finalData || {})
      } else if (section.uid === 'gmi_information') {
        // Access the correct array from the object
        const gmiSectionArr = finalData?.gmi_information_section || []

        // Find the are_you_gmi_sgs_certified object(s)
        const gmiCertified = gmiSectionArr
          .flat()
          .filter((item) => item.uid === 'are_you_gmi_sgs_certified')

        // Transform gmi_information_details_list
        const gmiDetails = gmiSectionArr
          .flat()
          .filter((item) => item.uid === 'gmi_information_details_list')
          .flatMap((item) =>
            Array.isArray(item.value)
              ? item.value.map((entry) => [
                  {
                    label: 'GMI Supplier ID',
                    value: entry.gmi_supplier_id,
                    uid: 'gmi_supplier_id',
                  },
                  {
                    label:
                      'Location Artwork / File Download\nContact Information - Email',
                    value: entry.gmi_email_id,
                    uid: 'gmi_email_id',
                  },
                  {
                    label: 'Certified For',
                    value: Array.isArray(entry.certified_for)
                      ? entry.certified_for.join(', ')
                      : entry.certified_for,
                    uid: 'certified_for',
                  },
                  {
                    label: 'ICC Profile',
                    value: entry.icc_profile,
                    uid: 'icc_profile',
                  },
                ])
              : [],
          )

        masterData = {
          are_you_gmi_sgs_certified: gmiCertified,
          gmi_information_details_list: gmiDetails,
        }
      } else if (section.uid === 'total_trim_capabilities') {
        masterData = {
          total_trim_capabilities: formatTotalTrimCapabilities(
            formattedRfi3Data?.total_trim_capability_list,
          ),
        }
      } else if (section.uid === 'production_outsourcing') {
        masterData = {
          production_outsourcing: formatTrimSubContractedList(
            formattedRfi3Data?.production_outsourcing_list,
            'production',
          ),
        }
      } else if (section.uid === 'service_outsourcing') {
        masterData = {
          service_outsourcing: formatTrimSubContractedList(
            formattedRfi3Data?.service_outsourcing_list,
            'service',
          ),
        }
      } else if (section.uid === 'research_and_development') {
        masterData = {
          research_and_development: formatResearchContactInfoList(
            formattedRfi3Data?.research_and_development_list,
          ),
        }
      } else if (section.uid === 'material_capability') {
        masterData = {
          material_capability: formatMaterialCapabilityList(
            formattedRfi3Data?.material_capability_list,
          ),
        }
      } else if (section.uid === 'in_house_manufacturing_process') {
        masterData = {
          in_house_manufacturing_process: formatInHouseManufacturingList(
            formattedRfi3Data?.in_house_manufacturing_process_list,
          ),
        }
      } else if (section.uid === 'internal_testing_methods') {
        masterData = {
          internal_testing_methods: formatInternalTestingList(
            formattedRfi3Data?.internal_testing_methods_list,
          ),
        }
      } else if (section.uid === 'record_packaging_capabilities') {
        masterData = {
          record_packaging_capabilities: formatPackagingCapabilityList(
            formattedRfi3Data?.record_packaging_capability_list,
          ),
        }
      } else if (section.uid === 'additional_business_capabilities') {
        masterData = {
          additional_business_capabilities:
            formatAdditionalBusinessCapabilities(formattedRfi3Data),
        }
      } else if (section.uid === 'beauty_formulation_capabilities') {
        masterData = formatBeautyFormulationCapabilities(
          formattedRfi3Data || {},
        )
      } else if (section.uid === 'beauty_packaging_capabilities') {
        masterData = formatBeautyFormulationCapabilities(
          formattedRfi3Data || {},
        )
      } else if (section.uid === 'key_factory_personnel') {
        masterData = {
          key_factory_personnel: formatContactList(
            formattedRfi3Data?.key_factory_personnel_list || [],
          ),
        }
      } else if (section.uid === 'fnb_mandatory_contacts') {
        masterData = {
          fnb_mandatory_contacts: [
            ...formatContactList(formattedRfi3Data?.contact_list || []),
            ...formatContactList(formattedRfi3Data?.other_contact_list || []),
          ],
        }
      } else if (section.uid === 'key_customer') {
        masterData = {
          key_customer: formatCustomerList(
            formattedRfi3Data?.key_customer_list || [],
            'key_customer',
          ),
        }
      } else if (section.uid === 'retailers_with_private_label') {
        masterData = {
          retailers_with_private_label: formatCustomerList(
            formattedRfi3Data?.retailers_with_private_label_list || [],
            'retailers_with_private_label',
          ),
        }
      } else {
        masterData = finalData
      }

      return {
        sectionId: section.uid,
        sectionName: section.section_name,
        sectionLabel: section.section_label,
        rfi3Data: {
          masterData,
          rfi3_questions_and_answers: section?.rfi3_questions.map(
            (questionItem) => ({
              ...questionItem,
              answer:
                formattedRfi3Data?.rfi3_answers?.find(
                  (answerItem) => answerItem.uid === questionItem.uid,
                )?.answer || '',
              options: questionItem.options
                ? resolveMasterOptionPath(rfiMasterData, questionItem.options)
                : undefined,
            }),
          ),
        },
      }
    }),
  }))
}

// --- PDF Data Mapping ---

/**
 * Returns true if the certificate name is a default certificate name, otherwise false.
 * @param {Object} certInfo - The certificate information object.
 * @param {number} manufacturingTypeId - The manufacturing type ID.
 */
export const isDefaultCertName = (certInfo, manufacturingTypeId) => {
  return (
    certInfo?.certificate_name?.toLowerCase() ===
      FACTORY_REGISTRATION_LICENSE ||
    (manufacturingTypeId === MANUFACTURING_TYPE_ID_TRIM &&
      certInfo?.certificate_name?.toLowerCase() ===
        TRIM_SUPPLIER_QUALIFICATION_PROGRAM)
  )
}

/**
 * Helper function to build firefly events
 */

export const buildTimelineEventData = (
  action,
  rfiObject,
  userInfoData,
  rfiType,
) => {
  const data = {
    event: {
      type: 'CLICK',
      id: TIMELINE_VIEW_NAME,
      value: rfiType,
    },
    supplier_info: {
      id: rfiObject.supplier_id,
      location_id: rfiObject.location_id,
      manufacturing_type: rfiObject?.manufacturing_type,
      action: action,
    },
    client: {
      page_name: TIMELINE_VIEW_NAME,
      employee_login_id: userInfoData?.id,
    },
  }
  return data
}

export const createFireflyEvent = (key, value, userInfoData, type) => {
  const strVal = String(value)

  console.log({ userInfoData })
  return {
    event: {
      type: 'CLICK',
      id: key,
      value: strVal,
    },
    client: {
      employee_login_id: userInfoData?.id,
    },
    mvs_user_info: {
      user_email_id: userInfoData?.email,
      user_full_name: userInfoData?.fullName,
      user_designation: userInfoData?.designation,
      user_family_type: userInfoData?.familyType,
    },
  }
}

// dynamic table section builder helpers

/**
 * Get the control type in uppercase
 * @param {Object} control - The control template object
 * @returns {string} The control type in uppercase
 */
export const getTableControlType = (control) => {
  return control?.type?.toUpperCase?.() || ''
}

/**
 * Get the display label for a control
 * @param {Object} control - The control template object
 * @returns {string} The label to display
 */
export const getTableControlLabel = (control) => {
  return control?.label_name || control?.label || control?.uid || ''
}

/**
 * Get the section title from a section template
 * @param {Object} sectionTemplate - The section template object
 * @returns {string} The section title
 */
export const getTableSectionTitle = (sectionTemplate) => {
  return (
    sectionTemplate?.sub_section_name ||
    sectionTemplate?.section_name ||
    sectionTemplate?.data?.row_header ||
    ''
  )
}

/**
 * Get the add button label from template
 * @param {Object} sectionTemplate - The section template object
 * @returns {string} The add button label
 */
export const getTableAddButtonLabel = (sectionTemplate) => {
  return sectionTemplate?.add_more?.label
}

/**
 * Get the delete button label from template
 * @param {Object} sectionTemplate - The section template object
 * @returns {string} The delete button label
 */
export const getTableDeleteButtonLabel = (sectionTemplate) => {
  return sectionTemplate?.remove?.label
}

/**
 * Check if a control should be rendered as a table column
 * @param {Object} control - The control template object
 * @param {Array} excludedTypes - Array of excluded control types
 * @returns {boolean} Whether the control should render as a column
 */
export const shouldRenderControlAsTableColumn = (control, excludedTypes) => {
  const controlType = getTableControlType(control)
  return !excludedTypes.includes(controlType)
}

/**
 * Filter controls to only those that should render as table columns
 * @param {Array} controls - Array of control template objects
 * @param {Array} excludedTypes - Array of excluded control types
 * @returns {Array} Filtered controls for table columns
 */
export const getTableControls = (controls, excludedTypes) => {
  if (!Array.isArray(controls)) return []
  return controls.filter((control) =>
    shouldRenderControlAsTableColumn(control, excludedTypes),
  )
}

/**
 * Safely return rows from section data
 * @param {Array} sectionData - The section data array
 * @returns {Array} The rows array
 */
export const getTableRows = (sectionData) => {
  return Array.isArray(sectionData) ? sectionData : []
}

/**
 * Build dropdown options for a table control
 * @param {Object} params
 * @param {Object} params.control - The control template object
 * @param {Object} params.rfiMasterData - The master data for dropdown options
 * @returns {Array} The dropdown options
 */
export const getTableDropdownOptions = ({ control, rfiMasterData }) => {
  if (!control?.options) return []

  try {
    return buildDropdownOptions(
      rfiMasterData,
      {
        nodeDotPath: control.options,
        property: control.property,
      },
      true,
      false,
    )
  } catch {
    return []
  }
}

/**
 * Build the path for a specific row in a section
 * @param {Object} params
 * @param {string} params.sectionPath - The base section path
 * @param {number} params.rowIndex - The row index
 * @returns {string} The row path
 */
export const buildTableRowPath = ({ sectionPath, rowIndex }) => {
  if (!sectionPath) return ''
  return `${sectionPath}[${rowIndex}]`
}

/**
 * Build metadata for a table cell control
 * @param {Object} params
 * @returns {Object} The control metadata
 */
export const buildTableControlMetadata = ({
  control,
  rowData,
  rowIndex,
  sectionPath,
  sectionTemplate,
  errorCache,
  isReadonly,
  dropdownOptions,
}) => {
  return {
    sectionPath: buildTableRowPath({ sectionPath, rowIndex }),
    errorCache,
    uid: control?.uid,
    inputMetadata: control,
    isDisabled: isReadonly || control?.isDisabled,
    readonly: isReadonly,
    dropdownOptions,
    rowIndex,
    rowData,
    sectionUid: sectionTemplate?.uid,
    isTableCell: true,
  }
}

/**
 * Build a control object for rendering inside a table cell (labels hidden)
 * @param {Object} params
 * @param {Object} params.control - The control template object
 * @param {Object} params.metadata - The built metadata
 * @returns {Object} The table cell control
 */
export const buildTableCellControl = ({ control, metadata }) => {
  return {
    ...control,
    label_name: '',
    label: '',
    hideLabel: true,
    metadata,
  }
}

/**
 * Check if the delete button should be disabled for a specific row
 * @param {Object} params
 * @param {Array} params.rows - The current rows
 * @param {number} params.rowIndex - The row index
 * @param {Object} params.sectionTemplate - The section template
 * @returns {boolean} Whether delete should be disabled
 */
export const shouldDisableTableDeleteButton = ({
  rows,
  rowIndex,
  sectionTemplate,
}) => {
  const firstRowMandatory =
    sectionTemplate?.remove?.first_row_mandatory === true

  if (rows.length <= 1) return true
  if (firstRowMandatory && rowIndex === 0) return true

  return false
}
